<template>
  <div v-if="programme" class="programme-card">
    <k-panel :hasContentToggle="true" :showContent="showProgrammeContent" @toggle="showProgrammeContent = $event" :title="programme.programme_name">
      <template #body>
        <k-panel :hasContentToggle="false" title="Programme Details" class="programme-details-panel sub-panel">
          <template #body>
            <k-table
              class="programme-details-table"
              :loading="!programme"
              :rows="kableRows"
              :headers="kableHeaders"
              :hideable="false"
              :panel="false"
              dateFormat="MMM d, yyyy"
            >
            </k-table>
          </template>
        </k-panel>
        <k-panel :hasContentToggle="false" title="Learner Support" class="learner-support-panel sub-panel">
          <template #body>
            <section v-for="role in learnerSupportRoles" :key="role.label" class="learner-support-role">
              <h3>{{ role.label }}</h3>
              <template v-if="role.person">
                <router-link v-if="$permissions.hasPermission('view_all_learners')" :to="{ name: 'dashboard_students', params: { studentId: role.person.user_id } }" :aria-label="`View ${role.person.name}`">
                  <b>{{ role.person.name }}</b><i class="fas fa-external-link-alt" aria-hidden="true"></i>
                </router-link>
                <p v-else class="mentor-name">{{ role.person.name }}</p>
                <p>{{ role.person.email }}</p>
              </template>
              <p v-else>{{ role.emptyMessage }}</p>
            </section>
          </template>
        </k-panel>
        <k-panel :hasContentToggle="false" title="PICS Episodes" class="pics-episodes-panel sub-panel">
          <template #body>
            <p>All PICS episodes linked to this programme.</p>
            <p class="pics-episodes-count">{{ picsEpisodeMessage }}</p>
            <k-table
              class="pics-episodes-table"
              :loading="!programme"
              :rows="picsKableRows"
              :headers="picsKableHeaders"
              :hideable="true"
              :panel="false"
              :show-controls="true"
              :defaultHeaders="visibleHeaders"
              dateFormat="MMM d, yyyy"
            >
            </k-table>
          </template>
        </k-panel>
      </template>
      <template #content-hidden>
        <div class="collapsed-info-container" aria-label="Collapsed programme summary">
          <p>Start date: <b>{{ programmeStartDate }}</b></p>
          <p>End date: <b>{{ programmeEndDate }}</b></p>
          <div class="collapsed-info-pics">
            <i class="fas fa-list-ul pics-episodes-icon" aria-hidden="true"></i>
            <span class="pics-episodes-message">{{ picsEpisodeMessage }}</span>
          </div>
        </div>
      </template>
    </k-panel>
  </div>
</template>

<style scoped>
  .sub-panel {
    background-color: var(--kate-panel);
    margin: 15px;
    box-shadow: var(--box-shadow);
    transition: all 0.2s ease-in-out;
  }

  .pics-programme-search-container {
    display: flex;
    justify-content: flex-end;
  }

  .learner-support-role {
    margin-bottom: 15px;
  }

  .pics-episodes-icon {
    color: var(--kate-episodes);
  }

  [data-theme="light"] .pics-episodes-icon {
    color: var(--kate-button-tertiary);
  }

  .collapsed-info-container p {
    margin: 0;
  }

  .collapsed-info-pics {
    display: flex;
    gap: 10px;
    align-items: center;
  }

  .pics-episodes-message {
    color: var(--kate-type-secondary);
  }

  .programme-card {
    margin-top: 20px;
  }

  .pics-episodes-count {
    color: var(--kate-type-secondary) !important;
    margin-top: 20px;
  }
</style>

<style>
  .programme-access-frozen {
    color: var(--kate-type-white);
  }

  [data-theme="light"] {
    .programme-access-frozen {
      color: var(--kate-type-dark);
    }
  }

  .programme-access-granted {
    color: var(--kate-type-success);
  }

  .programme-card h2 {
    margin-top: 10px;
  }
</style>

<script>
import KPanel from '@base-components/k-panel.vue';
import KTable from '@base-components/tables/display/k-table.vue';
import TimeMixin from '@mixins/time-mixins';

export default {
  name: 'IlrProgrammeCard',
  components: {
    KPanel,
    KTable,
  },
  mixins: [TimeMixin],

  props: {
    programme: {
      type: Object,
      required: true,
    },
    learnerInfo: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      showProgrammeContent: false,
      programmePicsEpisodes: [],
    };
  },

  computed: {
    programmeId() {
      return this.programme.programme_id;
    },

    programmeStartDate() {
      return this.formatDateString(this.programme.programme_start_date) || 'No start date';
    },

    programmeEndDate() {
      return this.formatDateString(this.programme.planned_end_date) || 'No end date';
    },

    picsEpisodeMessage() {
      if (this.programme.linked_episodes.length === 0) {
        return 'No linked PICS episodes found';
      }
      if (this.programme.linked_episodes.length === 1) {
        return '1 linked PICS episode found';
      }
      return `${this.programme.linked_episodes.length} linked PICS episodes found`;
    },

    kableHeaders() {
      return {
        product: {
          name: 'Product',
          type: 'string',
        },
        is_apprenticeship: {
          name: 'Apprenticeship',
          type: 'string',
        },
        programme_group: {
          name: 'Programme Groups',
          type: 'tags',
        },
        programme_access: {
          name: 'Programme Access',
          type: 'pill',
          options: {
            type: status => ({
              Frozen: 'danger',
              Granted: 'success',
            }[status] || 'neutral'),
          },
        },
        start_date: {
          name: 'Start Date',
          type: 'date',
        },
        end_date: {
          name: 'End Date',
          type: 'date',
        },
      };
    },
    kableRows() {
      return [
        {
          product: this.programme.product_name,
          is_apprenticeship: this.programme.is_apprenticeship ? 'Yes' : 'No',
          programme_group: {
            tags: this.programme.groups?.length > 0 ? this.programme.groups : null,
          },
          programme_access: this.programme.is_frozen ? 'Frozen' : 'Granted',
          start_date: this.programme.programme_start_date,
          end_date: this.programme.programme_planned_end_date,
        },
      ];
    },

    learnerCoach() {
      if (this.learnerInfo && this.learnerInfo.coaches && this.learnerInfo.coaches.length > 0) {
        return this.learnerInfo.coaches.find(x => x.programme_id === this.programme.programme_id);
      }
      return undefined;
    },

    learnerDataMentor() {
      if (this.learnerInfo && this.learnerInfo.data_mentors && this.learnerInfo.data_mentors.length > 0) {
        return this.learnerInfo.data_mentors.find(x => x.programme_id === this.programme.programme_id);
      }
      return undefined;
    },

    learnerLineManager() {
      if (this.learnerInfo && this.learnerInfo.line_managers && this.learnerInfo.line_managers.length > 0) {
        return this.learnerInfo.line_managers.find(x => x.programme_id === this.programme.programme_id);
      }
      return undefined;
    },

    learnerSupportRoles() {
      return [
        { label: 'Coach', person: this.learnerCoach, emptyMessage: 'No coach assigned' },
        { label: 'Data Mentor', person: this.learnerDataMentor, emptyMessage: 'No data mentor assigned' },
        { label: 'Line Manager', person: this.learnerLineManager, emptyMessage: 'No line manager assigned' },
      ];
    },

    picsKableHeaders() {
      return {
        episode_id: {
          name: 'Episode ID',
          type: 'string',
        },
        uln: {
          name: 'ULN',
          type: 'string',
        },
        full_name: {
          name: 'Learner Name',
          type: 'string',
        },
        standard: {
          name: 'Standard',
          type: 'string',
        },
        ilr_status: {
          name: 'Status',
          type: 'string',
        },
        start_date: {
          name: 'Start Date',
          type: 'date',
        },
        end_date: {
          name: 'End Date',
          type: 'date',
        },
      };
    },
    picsKableRows() {
      return this.programme.linked_episodes.map(episode => ({
        episode_id: episode.pics_aim_episode_id,
        uln: episode.uln,
        full_name: episode.pics_full_name,
        ilr_status: episode.ilr_status,
        start_date: this.formatDateString(episode.ilr_start_date),
        end_date: this.formatDateString(episode.ilr_planned_end_date),
        standard: this.programme.product_name,
      }));
    },

    visibleHeaders() {
      return ['uln', 'full_name', 'standard', 'ilr_status', 'start_date', 'end_date'];
    },
  },
};
</script>
